'use client';

import { useGetLatestReleaseNotes } from '@/lib/contentful';
import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const RELEASE_NOTES_KEY = 'release-notes-latest-read-date';

/**
 * Hook to track if the user has read the latest release note from Contentful
 * Stores the latest read date in localStorage and compares with Contentful data
 */
export function useReleaseNoteReadStatus() {
  // Fetch the single most recent release note from Contentful
  const { data: latestNotes, isLoading } = useGetLatestReleaseNotes({
    limit: 1,
  });

  // Store the last read timestamp as ISO string
  const [latestReadDate, setLatestReadDate] = useLocalStorage<string>(
    RELEASE_NOTES_KEY,
    new Date(0).toISOString() // Default: epoch — treat all notes as unread until user explicitly reads
  );

  const hasReadLatest = useMemo(() => {
    // If still loading or no notes available, assume they've read everything
    if (isLoading || !latestNotes || latestNotes.length === 0) {
      return true;
    }

    // Validate and parse the latest read date
    const latestReadTimestamp = Date.parse(latestReadDate);

    // If the stored date is invalid (NaN), assume they haven't read anything
    if (Number.isNaN(latestReadTimestamp)) {
      console.warn('Invalid latestReadDate in localStorage:', latestReadDate);
      return false;
    }

    // Safely get the latest note's timestamp
    const latestNote = latestNotes[0];
    if (!latestNote?.date) {
      console.warn('Latest release note has no date');
      return true; // No valid note to compare against
    }

    // Handle both Date objects and ISO date strings (from localStorage persistence)
    const latestNoteTimestamp =
      latestNote.date instanceof Date
        ? latestNote.date.getTime()
        : Date.parse(latestNote.date as unknown as string);

    // If the note timestamp is invalid, assume they've read everything
    if (Number.isNaN(latestNoteTimestamp)) {
      console.warn('Invalid date on latest release note:', latestNote.date);
      return true;
    }

    // User has read latest if the latest read date is >= latest note date
    return latestNoteTimestamp <= latestReadTimestamp;
  }, [latestReadDate, latestNotes, isLoading]);

  /**
   * Mark current time as latest read date
   */
  const markAsRead = useCallback(() => {
    setLatestReadDate(new Date().toISOString());
  }, [setLatestReadDate]);

  /**
   * Get the latest release note
   */
  const latestNote = latestNotes?.[0];

  return {
    hasReadLatest,
    latestReadDate,
    setLatestReadDate,
    markAsRead,
    latestNote,
    isLoading,
  };
}
